<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { requester } from '@ibgroup-tws-webapps/ratio.ui';
import { useUserState } from '@/stores/useUserState';

const { t } = useI18n();
import InputText from 'primevue/inputtext';
import Chip from 'primevue/chip';
import Button from 'primevue/button';
import {
  isValidEmail,
  categorizeShareResult,
  type BetSlipSharePayload,
  type ShareEmailResult,
} from '@/types/share.types';
import type { BetSlipItem } from '@/types/betslip.types';

const props = defineProps<{
  /** Market/election name for the email subject */
  marketName: string;
  /** Items to include in the share email */
  items: BetSlipItem[];
  /** Deep link URL for the bet slip (passed from parent wrapper) */
  betSlipLink: string;
}>();

const emit = defineEmits<{
  /** Emitted after successful share */
  success: [];
  /** Emitted when user cancels or closes */
  close: [];
}>();

// Constants
const MAX_RECIPIENTS = 5;

// State
const userState = useUserState();
const recipients = ref<string[]>([]);
const currentInput = ref('');
const errorMessage = ref('');
const isSending = ref(false);

// Share result states
const shareResult = ref<ShareEmailResult | null>(null);
const rateLimitCountdown = ref(0);
let rateLimitInterval: ReturnType<typeof setInterval> | null = null;

// Computed
const canAddMore = computed(() => recipients.value.length < MAX_RECIPIENTS);
const canSend = computed(() => recipients.value.length > 0 && !isSending.value && userState.authenticated);

// UI state computed properties
const showSuccess = computed(() => shareResult.value?.status === 'success');
const showRateLimit = computed(() => shareResult.value?.status === 'rate_limited');
const showPartial = computed(() => shareResult.value?.status === 'partial');
const showError = computed(() => shareResult.value?.status === 'error' && shareResult.value !== null);

// Format countdown for display
const formattedCountdown = computed(() => {
  const total = rateLimitCountdown.value;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
});

// Methods
function addRecipient() {
  const email = currentInput.value.trim().toLowerCase();
  errorMessage.value = '';

  if (!email) return;

  if (!canAddMore.value) {
    errorMessage.value = t('maxRecipientsAllowed', { max: MAX_RECIPIENTS });
    return;
  }

  if (!isValidEmail(email)) {
    errorMessage.value = t('invalidEmailAddress');
    return;
  }

  if (recipients.value.includes(email)) {
    errorMessage.value = t('emailAlreadyAdded');
    return;
  }

  recipients.value.push(email);
  currentInput.value = '';
}

function removeRecipient(email: string) {
  const index = recipients.value.indexOf(email);
  if (index !== -1) {
    recipients.value.splice(index, 1);
  }
}

function handleKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
    e.preventDefault();
    addRecipient();
  }
}

async function sendShare() {
  if (!canSend.value) return;

  isSending.value = true;
  shareResult.value = null;
  errorMessage.value = '';

  const payload: BetSlipSharePayload = {
    template: 'SHARE_BETSLIP_EMAIL',
    recipients: recipients.value,
    accountId: userState.account || '',
    shareEmailBetSlipDTO: {
      title: props.marketName,
      betSlipLink: props.betSlipLink,
      appName: 'FORECASTTRADER',
      items: props.items.map((item, index) => ({
        title: item.title,
        titleColor: item.titleColor || '',
        landPageLink: item.link || '',
        imageLink: item.image || '',
        displayOrder: index + 1,
        chips: item.chips.map((chip, chipIndex) => ({
          label: chip.label,
          displayOrder: chipIndex + 1,
        })),
      })),
    },
  };

  try {
    const response = await requester.request('ibsales.rest.service.sso/shareEmail', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    shareResult.value = categorizeShareResult(response, data);

    if (shareResult.value.status === 'success') {
      // Auto-close after 3 seconds on success
      setTimeout(() => {
        emit('success');
      }, 3000);
    } else if (shareResult.value.status === 'rate_limited') {
      startRateLimitCountdown(shareResult.value.reset);
    }
  } catch (error) {
    shareResult.value = {
      status: 'error',
      message: error instanceof Error ? error.message : t('failedToSendEmail'),
    };
  } finally {
    isSending.value = false;
  }
}

function startRateLimitCountdown(resetTime: string) {
  const reset = new Date(resetTime);
  const now = new Date();
  rateLimitCountdown.value = Math.max(0, Math.ceil((reset.getTime() - now.getTime()) / 1000));

  if (rateLimitInterval) {
    clearInterval(rateLimitInterval);
  }

  rateLimitInterval = setInterval(() => {
    if (rateLimitCountdown.value > 0) {
      rateLimitCountdown.value--;
    } else {
      clearInterval(rateLimitInterval!);
      rateLimitInterval = null;
      shareResult.value = null;
    }
  }, 1000);
}

function resetForm() {
  shareResult.value = null;
  errorMessage.value = '';
}

// Cleanup
onUnmounted(() => {
  if (rateLimitInterval) {
    clearInterval(rateLimitInterval);
  }
});
</script>

<template>
  <div class="share-betslip">
    <!-- Success State -->
    <div v-if="showSuccess" class="flex flex-col items-center justify-center py-12 text-center">
      <div class="i-mdi-check-circle text-6xl text-success mb-4" />
      <p class="text-xl font-semibold text-on-surface">{{ t('emailsSentSuccessfully') }}</p>
    </div>

    <!-- Rate Limit State -->
    <div v-else-if="showRateLimit" class="flex flex-col items-center justify-center py-12 text-center">
      <div class="i-mdi-clock-outline text-6xl text-warning mb-4" />
      <p class="text-xl font-semibold text-on-surface mb-2">{{ t('sharingLimitReached') }}</p>
      <p class="text-on-surface-variant mb-4">
        {{ t('pleaseWait') }} <span class="font-bold text-error">{{ formattedCountdown }}</span>
        {{ t('beforeTryingAgain') }}
      </p>
      <Button :label="t('back')" severity="secondary" @click="resetForm" />
    </div>

    <!-- Form State (also shown for partial/error with back option) -->
    <div v-else>
      <!-- Partial Success Warning -->
      <div v-if="showPartial && shareResult?.status === 'partial'" class="rounded-lg bg-warning-container p-4">
        <div class="flex items-start gap-3">
          <div class="i-mdi-alert text-xl text-warning" />
          <div class="flex-1">
            <p class="font-semibold text-on-surface mb-2">{{ t('someEmailsNotSent') }}</p>
            <div v-if="shareResult.data.successfulRecipients.length" class="mb-2">
              <p class="text-sm text-on-surface-variant">{{ t('sentTo') }}</p>
              <ul class="text-sm list-disc list-inside">
                <li v-for="email in shareResult.data.successfulRecipients" :key="email">{{ email }}</li>
              </ul>
            </div>
            <div v-if="shareResult.data.failedRecipients.length">
              <p class="text-sm text-on-surface-variant">{{ t('failed') }}</p>
              <ul class="text-sm list-disc list-inside text-error">
                <li v-for="email in shareResult.data.failedRecipients" :key="email">{{ email }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Error State -->
      <div v-if="showError && shareResult?.status === 'error'" class="rounded-lg bg-error-container p-4">
        <div class="flex items-start gap-3">
          <div class="i-mdi-alert-circle text-xl text-error" />
          <div class="flex-1">
            <p class="font-semibold text-on-surface">{{ t('failedToSendEmails') }}</p>
            <p class="text-sm text-on-surface-variant">{{ shareResult.message }}</p>
          </div>
        </div>
      </div>

      <!-- Email Input -->
      <div>
        <label class="text-sm font-medium text-on-surface mb-2 block">{{ t('sendTo') }}</label>
        <div>
          <div
            class="flex flex-wrap items-center gap-2 min-h-[44px] p-2 border rounded-lg border-outline bg-surface focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-all overflow-hidden"
          >
            <Chip
              v-for="email in recipients"
              :key="email"
              :label="email"
              removable
              @remove="removeRecipient(email)"
              class="!py-1 max-w-full [&_.p-chip-label]:truncate [&_.p-chip-label]:max-w-[calc(100%-1.5rem)]"
            />
            <InputText
              v-model="currentInput"
              :placeholder="recipients.length === 0 ? t('enterEmailAddresses') : ''"
              class="flex-1 min-w-[170px] !border-0 !shadow-none !p-0 !bg-transparent"
              :disabled="!canAddMore"
              @keydown="handleKeydown"
              @blur="addRecipient"
            />
            <span class="text-xs text-on-surface-variant whitespace-nowrap ml-auto">{{ t('pressEnterToAdd') }}</span>
          </div>
          <p v-if="errorMessage" class="text-sm text-error mt-1">{{ errorMessage }}</p>
          <p class="text-xs text-on-surface-variant mt-1">{{ t('maxRecipientsInfo', { max: MAX_RECIPIENTS }) }}</p>
        </div>
      </div>

      <!-- Share Button -->
      <div class="pt-2">
        <Button
          :label="t('share_send')"
          :disabled="!canSend"
          icon="ibi-share-send"
          :loading="isSending"
          @click="sendShare"
        />
      </div>
    </div>
  </div>
</template>
